import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Clock, MapPin, Bell, Link2, CreditCard,
  Save, Loader2, Plus, X, ChevronRight, Percent, Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const DAYS = [
  { key: 'mon', label: 'Пн' },
  { key: 'tue', label: 'Вт' },
  { key: 'wed', label: 'Ср' },
  { key: 'thu', label: 'Чт' },
  { key: 'fri', label: 'Пт' },
  { key: 'sat', label: 'Сб' },
  { key: 'sun', label: 'Вс' },
];

const TIMEZONES = [
  'America/Vancouver',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Toronto',
  'America/Halifax',
  'America/St_Johns',
];

const DEFAULT_SETTINGS = {
  name: 'CGC Services',
  phone: '',
  email: '',
  website: '',
  address: '',
  city: 'Vancouver',
  province: 'BC',
  postal_code: '',
  timezone: 'America/Vancouver',
  currency: 'CAD',
  tax_rate_percent: 5.0,
  tax_name: 'GST',
  corporate_tax_rate: 15.0,
  cofounders: [
    { name: 'Иван', share: 34.0 },
    { name: 'Вова', share: 33.0 },
    { name: 'Женя', share: 33.0 },
  ],
  min_wage_bc: 17.40,
  work_days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
  work_start: '07:00',
  work_end: '18:00',
  buffer_minutes: 30,
  advance_booking_days: 90,
  service_cities: [],
  service_radius_km: 50,
};

export default function SettingsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCity, setNewCity] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/company/settings');
      setSettings({ ...DEFAULT_SETTINGS, ...res.data });
    } catch {
      toast.error('Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/company/settings', settings);
      toast.success('Настройки сохранены');
    } catch {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const toggleDay = (day) => {
    const days = settings.work_days.includes(day)
      ? settings.work_days.filter(d => d !== day)
      : [...settings.work_days, day];
    set('work_days', days);
  };

  const addCity = () => {
    const city = newCity.trim();
    if (!city || settings.service_cities.includes(city)) return;
    set('service_cities', [...settings.service_cities, city]);
    setNewCity('');
  };

  const removeCity = (city) => {
    set('service_cities', settings.service_cities.filter(c => c !== city));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Настройки</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Управление параметрами компании</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Сохранить
        </Button>
      </div>

      {/* ── 1. Компания ───────────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-accent" /> Компания
          </CardTitle>
          <CardDescription>Основная информация о компании</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Название компании</Label>
            <Input value={settings.name} onChange={e => set('name', e.target.value)} placeholder="CGC Services" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Телефон</Label>
              <Input value={settings.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+1 604 000 0000" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={settings.email || ''} onChange={e => set('email', e.target.value)} placeholder="info@company.com" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Веб-сайт</Label>
            <Input value={settings.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://company.com" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Адрес офиса</Label>
            <Input value={settings.address || ''} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1 space-y-1">
              <Label className="text-xs">Город</Label>
              <Input value={settings.city || ''} onChange={e => set('city', e.target.value)} placeholder="Vancouver" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Провинция</Label>
              <Input value={settings.province || ''} onChange={e => set('province', e.target.value)} placeholder="BC" maxLength={2} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Индекс</Label>
              <Input value={settings.postal_code || ''} onChange={e => set('postal_code', e.target.value)} placeholder="V6B 2N9" maxLength={7} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Globe className="h-3 w-3" /> Часовой пояс</Label>
            <select
              value={settings.timezone}
              onChange={e => set('timezone', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz.replace('America/', '')}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* ── 2. Налоги и валюта ────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Percent className="h-4 w-4 text-accent" /> Налоги и валюта
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Название налога</Label>
              <Input value={settings.tax_name || 'GST'} onChange={e => set('tax_name', e.target.value)} placeholder="GST" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ставка (%)</Label>
              <Input
                type="number" min="0" max="30" step="0.5"
                value={settings.tax_rate_percent}
                onChange={e => set('tax_rate_percent', parseFloat(e.target.value) || 0)}
                placeholder="5"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Валюта</Label>
            <select
              value={settings.currency}
              onChange={e => set('currency', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="CAD">CAD — Канадский доллар</option>
              <option value="USD">USD — Доллар США</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Корпоративный налог (%)</Label>
            <Input
              type="number" min="0" max="50" step="0.5"
              value={settings.corporate_tax_rate}
              onChange={e => set('corporate_tax_rate', parseFloat(e.target.value) || 0)}
              placeholder="15"
            />
            <p className="text-xs text-muted-foreground">Ориентировочно: федеральный ~9% + BC ~2–4% (SBD). Уточните у бухгалтера.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Мин. ставка BC ($/час)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={settings.min_wage_bc}
              onChange={e => set('min_wage_bc', parseFloat(e.target.value) || 0)}
              placeholder="17.40"
            />
            <p className="text-xs text-muted-foreground">Пересматривается ежегодно правительством BC. Текущая: $17.40 (2025).</p>
          </div>
        </CardContent>
      </Card>

      {/* ── 2.5. Соучредители ──────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Percent className="h-4 w-4 text-accent" /> Доли соучредителей
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(settings.cofounders || []).map((cf, i) => (
            <div key={i} className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Имя</Label>
                <Input
                  value={cf.name}
                  onChange={e => {
                    const updated = [...settings.cofounders];
                    updated[i] = { ...updated[i], name: e.target.value };
                    set('cofounders', updated);
                  }}
                  placeholder="Имя"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Доля (%)</Label>
                <Input
                  type="number" min="0" max="100" step="0.1"
                  value={cf.share}
                  onChange={e => {
                    const updated = [...settings.cofounders];
                    updated[i] = { ...updated[i], share: parseFloat(e.target.value) || 0 };
                    set('cofounders', updated);
                  }}
                  placeholder="34"
                />
              </div>
            </div>
          ))}
          {(() => {
            const total = (settings.cofounders || []).reduce((s, c) => s + (c.share || 0), 0);
            return total !== 100 ? (
              <p className="text-xs text-destructive">⚠️ Сумма долей = {total.toFixed(1)}% (должно быть 100%)</p>
            ) : (
              <p className="text-xs text-green-600">✓ Сумма долей = 100%</p>
            );
          })()}
        </CardContent>
      </Card>

      {/* ── 3. Рабочие часы ──────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-accent" /> Рабочие часы
          </CardTitle>
          <CardDescription>Дни работы и время приёма заказов</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Days toggles */}
          <div className="space-y-1">
            <Label className="text-xs">Рабочие дни</Label>
            <div className="flex gap-1.5 flex-wrap">
              {DAYS.map(d => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => toggleDay(d.key)}
                  className={cn(
                    "w-10 h-10 rounded-lg text-sm font-medium transition-colors border",
                    settings.work_days.includes(d.key)
                      ? "bg-accent text-accent-foreground border-accent"
                      : "bg-muted/30 text-muted-foreground border-border hover:border-accent/50"
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Начало рабочего дня</Label>
              <input
                type="time"
                value={settings.work_start}
                min="05:00" max="12:00" step="1800"
                onChange={e => set('work_start', e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Конец рабочего дня</Label>
              <input
                type="time"
                value={settings.work_end}
                min="14:00" max="23:00" step="1800"
                onChange={e => set('work_end', e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Буфер между заказами (мин)</Label>
              <select
                value={settings.buffer_minutes}
                onChange={e => set('buffer_minutes', parseInt(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {[0, 15, 30, 45, 60, 90].map(v => (
                  <option key={v} value={v}>{v === 0 ? 'Без буфера' : `${v} мин`}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Запись вперёд (дней)</Label>
              <select
                value={settings.advance_booking_days}
                onChange={e => set('advance_booking_days', parseInt(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {[30, 60, 90, 180, 365].map(v => (
                  <option key={v} value={v}>{v} дней</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 4. Зоны обслуживания ─────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4 text-accent" /> Зоны обслуживания
          </CardTitle>
          <CardDescription>Города и районы где работает компания</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Cities list */}
          <div className="flex flex-wrap gap-2 min-h-[40px]">
            {settings.service_cities.length === 0 ? (
              <p className="text-xs text-muted-foreground self-center">Нет городов — обслуживаем всех</p>
            ) : (
              settings.service_cities.map(city => (
                <Badge key={city} variant="secondary" className="gap-1 text-sm pr-1">
                  {city}
                  <button onClick={() => removeCity(city)} className="hover:text-destructive ml-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={newCity}
              onChange={e => setNewCity(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCity()}
              placeholder="Добавить город..."
              className="flex-1"
            />
            <Button type="button" size="sm" onClick={addCity} disabled={!newCity.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Максимальный радиус (км)</Label>
            <select
              value={settings.service_radius_km}
              onChange={e => set('service_radius_km', parseInt(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {[10, 20, 30, 50, 75, 100, 150, 200].map(v => (
                <option key={v} value={v}>{v} км</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* ── 5. Быстрые ссылки ────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4 text-accent" /> Другие настройки
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[
            { icon: Bell, label: 'Уведомления', desc: 'Email и push-уведомления', path: '/notifications/settings' },
            { icon: CreditCard, label: 'Интеграции', desc: 'Stripe, Twilio, Google Calendar', path: '/integrations' },
            { icon: CreditCard, label: 'Платежи', desc: 'История и методы оплаты', path: '/payments' },
          ].map(({ icon: Icon, label, desc, path }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
            >
              <Icon className="h-5 w-5 text-accent shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Save button (bottom) */}
      <Button onClick={handleSave} disabled={saving} className="w-full" size="lg">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Сохранить все настройки
      </Button>
    </div>
  );
}
